// 入札編集モーダルの 入札方式 / 戦略モードのスケジュール (AC-BIN-005b) form の
// 初期値・行追加・検証 schema。 送信値の組み立ては write seam
// (`intradayOrderWrite.ts` の `withIntradayStrategySchedule`)。
import { timestampDate } from '@bufbuild/protobuf/wkt'
import {
  type BiddingIntradayOrder,
  BiddingIntradayOrderBidMode,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { TFunction } from 'i18next'
import { z } from 'zod'

import { type BidLimits, perBidVolumeLimitExceeded } from '~/models/bidLimits'
import {
  JEPX_PRICE_CONSTRAINT,
  JEPX_VOLUME_CONSTRAINT,
  isPlainDecimalInput,
  violatesJepxStep,
} from '~/models/numberInputConstraint'
import { decimalVolumeToMw } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'
import type { IntradayScheduleRowInput } from '~/pages/IntradayBidding/models/intradayOrderWrite'
import { INTRADAY_P8_AVAILABLE } from '~/pages/IntradayBidding/models/intradayP8Availability'
import {
  type ScheduleAppliedAtViolation,
  resolveScheduleAppliedAt,
  toScheduleDayAndTime,
  validateScheduleAppliedAt,
} from '~/pages/IntradayBidding/models/strategySchedule'

export type IntradayStrategyFormValues = {
  bidMode: 'normal' | 'strategy'
  schedule: IntradayScheduleRowInput[]
}

/**
 * 追加行の既定の適用日時 (直前の段が無い時)。 開場 (前日 17:00) の後で、 どのコマ
 * でも GC (最も早いコマ 1 で前日 23:00) より手前に入る (design `bidding/intraday.html`
 * `addSchedRow`)。
 */
const FIRST_ROW_DEFAULT = { day: 'dayBefore', timeHm: '19:30' } as const

/**
 * スケジュールの段数の上限。 Update RPC の `schedule_entries` の
 * `repeated.max_items` (proto) と揃える。
 */
export const MAX_INTRADAY_SCHEDULE_ROWS = 48

/** 行追加で直前の段からずらす分数 (mock-app BidEditDialog.AddScheduleEntry)。 */
const NEXT_ROW_STEP_MINUTES = 30

const volumeMwString = (d: BiddingIntradayOrder['volumeMwh']): string => {
  const mw = decimalVolumeToMw(d)
  return mw === undefined ? '' : String(mw)
}

/** 保存済みの札の入札方式。 表示専用の入札編集モーダルは、 この方式を選んだ状態で出す (AC-BIN-133)。 */
export const savedBidModeOf = (
  order: BiddingIntradayOrder,
): IntradayStrategyFormValues['bidMode'] =>
  order.bidMode === BiddingIntradayOrderBidMode.STRATEGY ? 'strategy' : 'normal'

/**
 * 編集できる下書きの入札編集モーダルを開いた時に選ぶ入札方式。 戦略が P8 の機能 (AC-BIN-133) で
 * `INTRADAY_P8_AVAILABLE` が false の間は選べないため、 保存済みの札が戦略でも「通常」で開く
 * (保存すると通常の札になる)。
 */
const initialBidModeOf = (
  order: BiddingIntradayOrder,
): IntradayStrategyFormValues['bidMode'] =>
  INTRADAY_P8_AVAILABLE ? savedBidModeOf(order) : 'normal'

/** 保存済みの札から form の初期値を組む。 */
export const toIntradayStrategyFormDefaults = (
  order: BiddingIntradayOrder,
  deliveryDate: Date,
): IntradayStrategyFormValues => ({
  bidMode: initialBidModeOf(order),
  schedule: order.scheduleEntries.flatMap((entry) => {
    if (entry.appliedAt === undefined) return []
    const dayAndTime = toScheduleDayAndTime(
      timestampDate(entry.appliedAt),
      deliveryDate,
    )
    // 取引窓の外の段は backend が保存を拒否するので通常は届かない。 届いた場合も
    // 前日 / 当日 + 時刻で表せないので入力欄には戻さない。
    if (dayAndTime === undefined) return []
    return [
      {
        day: dayAndTime.day,
        timeHm: dayAndTime.timeHm,
        volumeMw: volumeMwString(entry.volumeMw),
        priceYenPerKwh: entry.priceYenPerKwh?.value ?? '',
      },
    ]
  }),
})

/**
 * 「行を追加」 の新しい段。 適用日時は直前の段の 30 分後 (直前の段が無い / 時刻未入力
 * なら前日 19:30)、 量・価格は直前の段 (無ければ札の現在値) を引き継ぐ。 30 分後が
 * 前日 / 当日で表せない (= 当日を越える) 場合は直前の段と同じ日時のまま置き、 重複
 * として入力段の検証に委ねる。
 */
export const nextIntradayScheduleRow = (
  rows: IntradayScheduleRowInput[],
  order: BiddingIntradayOrder,
  deliveryDate: Date,
): IntradayScheduleRowInput => {
  const last = rows.at(-1)
  const volumeMw = last?.volumeMw ?? volumeMwString(order.volumeMwh)
  const priceYenPerKwh =
    last?.priceYenPerKwh ?? order.priceYenPerKwh?.value ?? ''
  const lastAt =
    last === undefined
      ? undefined
      : resolveScheduleAppliedAt({
          day: last.day,
          timeHm: last.timeHm,
          deliveryDate,
        })
  if (last === undefined || lastAt === undefined) {
    return { ...FIRST_ROW_DEFAULT, volumeMw, priceYenPerKwh }
  }
  const next = toScheduleDayAndTime(
    new Date(lastAt.getTime() + NEXT_ROW_STEP_MINUTES * 60_000),
    deliveryDate,
  ) ?? { day: last.day, timeHm: last.timeHm }
  return { ...next, volumeMw, priceYenPerKwh }
}

const isValidVolume = (s: string): boolean => {
  const n = Number(s)
  return (
    Number.isFinite(n) &&
    n >= JEPX_VOLUME_CONSTRAINT.min &&
    n <= JEPX_VOLUME_CONSTRAINT.max &&
    !violatesJepxStep(n, JEPX_VOLUME_CONSTRAINT.step)
  )
}

// 価格は入力した文字列のまま RPC に載せる (数量は `toFixed(1)` で整形して載せる) ので、
// `<input type="number">` が通す指数表記 (`1e1`) は backend の小数検証が拒否する前に弾く。
const isValidPrice = (s: string): boolean => {
  const n = Number(s)
  return (
    isPlainDecimalInput(s) &&
    Number.isFinite(n) &&
    n >= JEPX_PRICE_CONSTRAINT.min &&
    n <= JEPX_PRICE_CONSTRAINT.max &&
    !violatesJepxStep(n, JEPX_PRICE_CONSTRAINT.step)
  )
}

/**
 * 入札方式 / スケジュール form の検証 schema。 行の検証は戦略モードの時だけ行う
 * (= 通常モードに戻した時、 画面に残っている段の入力で保存を止めない。 通常モードの
 * 保存ではスケジュールを送らない)。
 *
 * 適用日時の範囲 (開場 ≤ 適用日時 < GC) の違反は段ごとの時刻欄を invalid にする
 * (文言は段番号付きで上部 Alert に集約される)。
 * `violationMessage` は違反種別 → 文言 (基準時刻入り) を呼び出し側が決める。
 */
export const buildIntradayStrategyFormSchema = ({
  t,
  deliveryDate,
  koma,
  violationMessage,
  limits,
  order,
}: {
  t: TFunction
  deliveryDate: Date
  koma: number
  violationMessage: (violation: ScheduleAppliedAtViolation) => string
  /** settings「入札制限」 (時間前)。 各段の量を上限数量(1札) と照合する (AC-BIN-100)。 */
  limits: BidLimits
  /** 札のエリア・売買 (上限数量(1札) を引く単位)。 */
  order: Pick<BiddingIntradayOrder, 'areaCode' | 'direction'>
}): z.ZodType<IntradayStrategyFormValues, IntradayStrategyFormValues> =>
  z
    .object({
      bidMode: z.enum(['normal', 'strategy']),
      schedule: z.array(
        z.object({
          day: z.enum(['dayBefore', 'deliveryDay']),
          timeHm: z.string(),
          volumeMw: z.string(),
          priceYenPerKwh: z.string(),
        }),
      ),
    })
    .superRefine((v, ctx) => {
      if (v.bidMode !== 'strategy') return
      if (v.schedule.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['schedule'],
          message: t('page:biddingIntraday.strategy.scheduleRequired'),
        })
        return
      }
      const seen = new Set<number>()
      // 段のエラーは欄脇ではなく上部の Alert に出るので、 どの段かを文言に含める。
      const rowMessage = (index: number, message: string): string =>
        t('page:biddingIntraday.strategy.rowMessage', {
          index: index + 1,
          message,
        })
      for (const [i, row] of v.schedule.entries()) {
        const appliedAt = resolveScheduleAppliedAt({
          day: row.day,
          timeHm: row.timeHm,
          deliveryDate,
        })
        if (appliedAt === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['schedule', i, 'timeHm'],
            message: rowMessage(
              i,
              t('page:biddingIntraday.strategy.timeRequired'),
            ),
          })
        } else {
          const violation = validateScheduleAppliedAt({
            appliedAt,
            deliveryDate,
            koma,
          })
          if (violation !== undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['schedule', i, 'timeHm'],
              message: rowMessage(i, violationMessage(violation)),
            })
          } else if (seen.has(appliedAt.getTime())) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['schedule', i, 'timeHm'],
              message: rowMessage(
                i,
                t('page:biddingIntraday.strategy.duplicateAppliedAt'),
              ),
            })
          }
          seen.add(appliedAt.getTime())
        }
        // 未入力と範囲外は文言が違う (AC-BIN-052)。
        if (row.volumeMw === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['schedule', i, 'volumeMw'],
            message: rowMessage(
              i,
              t('page:biddingIntraday.strategy.volumeRequired'),
            ),
          })
        } else if (!isValidVolume(row.volumeMw)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['schedule', i, 'volumeMw'],
            message: rowMessage(
              i,
              t('page:biddingIntraday.strategy.volumeRange'),
            ),
          })
        } else {
          // AC-BIN-100: 各段は 1 札として板に立つので、 上限数量(1札) を超える段は
          // 保存をブロックする (backend も同じ判定で拒否する)。
          const volumeMw = Number(row.volumeMw)
          const max = perBidVolumeLimitExceeded(
            limits,
            order.areaCode,
            order.direction,
            volumeMw,
          )
          if (max !== undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['schedule', i, 'volumeMw'],
              message: rowMessage(
                i,
                t('page:biddingIntraday.form.volumeExceedsPerBidLimit', {
                  max,
                  value: volumeMw,
                }),
              ),
            })
          }
        }
        if (!isValidPrice(row.priceYenPerKwh)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['schedule', i, 'priceYenPerKwh'],
            message: rowMessage(
              i,
              t('page:biddingIntraday.strategy.priceRange'),
            ),
          })
        }
      }
    })
